import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Container,
  IconButton,
  Button,
} from '@mui/material';
import {
  Event as EventIcon,
  Place as PlaceIcon,
  Person as PersonIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const AmplifiedEventDetails = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { event } = state || {};

  if (!event) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: '#f8f9fa',
        }}
      >
        <Typography variant="h6" color="error" sx={{ mb: 3 }}>
          No event data available
        </Typography>
        <Button
          variant="contained"
          onClick={() => navigate('/amplified-events')}
          sx={{ bgcolor: '#6200ee', '&:hover': { bgcolor: '#7f39fb' } }}
        >
          Back to Events
        </Button>
      </Box>
    );
  }

  const sections = [
    { title: 'Event Objective', content: event.objective },
    { title: 'Target Audience', content: event.targetAudience },
    { title: 'Event Theme', content: event.eventTheme || 'N/A' },
    { title: 'Schedule', content: event.schedule },
    { title: 'Key Speakers', content: event.keySpeakers || 'N/A' },
    { title: 'Workshops', content: event.workshops || 'N/A' },
    { title: 'Entry Requirements', content: event.entryRequirements || 'None' },
    { title: 'Parking & Transport', content: event.parkingTransport || 'N/A' },
    { title: 'Catering & Refreshments', content: event.cateringRefreshments || 'N/A' },
    { title: 'Dress Code', content: event.dressCode || 'N/A' },
    { title: 'Sponsorships', content: event.sponsorships || 'N/A' },
    { title: 'Contact Information', content: event.contactInformation },
  ];

  return (
    <Container maxWidth="md" sx={{ py: 4, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            <ArrowBackIcon sx={{ color: '#6200ee' }} />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2c3e50', textAlign: 'center', flex: 1 }}>
            {event.eventName}
          </Typography>
        </Box>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <Paper sx={{ p: 3, mb: 3, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <EventIcon sx={{ color: '#6200ee', mr: 2, fontSize: 24 }} />
            <Typography variant="body1" sx={{ color: '#2c3e50' }}>
              {new Date(event.dateTime).toLocaleString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PlaceIcon sx={{ color: '#6200ee', mr: 2, fontSize: 24 }} />
            <Typography variant="body1" sx={{ color: '#2c3e50' }}>
              {event.venue}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <PersonIcon sx={{ color: '#6200ee', mr: 2, fontSize: 24 }} />
            <Typography variant="body1" sx={{ color: '#2c3e50' }}>
              Organized by: {event.organizer}
            </Typography>
          </Box>
        </Paper>
      </motion.div>
      {sections.map((section, index) => (
        <motion.div
          key={section.title}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
        >
          <Paper sx={{ p: 3, mb: 3, bgcolor: '#fff', borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#6200ee', mb: 2 }}>
              {section.title}
            </Typography>
            <Typography variant="body1" sx={{ color: '#2c3e50', lineHeight: 1.6 }}>
              {section.content}
            </Typography>
          </Paper>
        </motion.div>
      ))}
    </Container>
  );
};

export default AmplifiedEventDetails;